import React, { useCallback, useEffect, useState } from "react";
import { FileText, Package, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { fmtDate, money } from "@/lib/constants";
import { useLang } from "@/i18n";

/** Verhuur bij een boeking: eigen periode, beschikbaarheid en inleveren. */
export const RentalBlock = ({ reservationId, start, end, lang: guestLang }) => {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [add, setAdd] = useState(null);
  const [free, setFree] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [i, r] = await Promise.all([api.get("/rental-items"), api.get(`/rentals?reservation_id=${reservationId}`)]);
      setItems(i.data.filter((x) => x.active !== false)); setRows(r.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [reservationId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!add?.item_id || !add.start || !add.end || add.end <= add.start) { setFree(null); return; }
    api.get(`/rental-items/${add.item_id}/availability?start=${add.start}&end=${add.end}`)
      .then(({ data }) => setFree(data)).catch(() => setFree(null));
  }, [add?.item_id, add?.start, add?.end]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/rentals", { reservation_id: reservationId, item_id: add.item_id, qty: Number(add.qty) || 1, start: add.start, end: add.end });
      setAdd(null); await load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const giveBack = async (r) => {
    const day = new Date().toISOString().slice(0, 10);
    try {
      await api.post(`/rentals/${r.id}/return`, { date: day > r.start && day <= r.end ? day : r.end });
      await load(); toast.success(t("rental_returned"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const deposit = async (r, action) => {
    try { await api.post(`/rentals/${r.id}/deposit`, { action }); await load(); toast.success(t("saved")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openDepositPdf = async (r) => {
    try {
      const { data } = await api.get(`/rentals/${r.id}/deposit-pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (r) => {
    try { await api.delete(`/rentals/${r.id}`); await load(); toast.success(t("deleted")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const label = (r) => r.names?.[guestLang] || r.names?.[lang] || r.names?.nl || r.names?.en || r.code;

  return (
    <div className="rounded-lg border border-border bg-muted/40/70 p-3 space-y-2" data-testid="rental-block">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Package className="h-3.5 w-3.5 text-sky-600" />{t("rentals")}
        </p>
        <Button size="sm" variant="outline" className="h-7"
          onClick={() => setAdd({ item_id: "", qty: 1, start, end })} data-testid="rental-add-button">
          <Plus className="h-3.5 w-3.5 mr-1" />{t("rental_add")}
        </Button>
      </div>
      {rows.length === 0 ? (
        <p className="text-[12px] text-muted-foreground">{t("rental_none")}</p>
      ) : rows.map((r) => (
        <div key={r.id} className="flex items-center justify-between gap-2 text-[12px]" data-testid={`rental-row-${r.id}`}>
          <span className="text-foreground/90">
            <b>{r.qty}× {label(r)}</b> · {fmtDate(r.start)} → {fmtDate(r.returned_on || r.end)}
            <span className="text-muted-foreground"> · {money(r.price)}/{t(`pricing_${r.pricing}`).toLowerCase()}
              {r.deposit ? ` · ${t("rental_deposit")} ${money(r.deposit * r.qty)}` : ""}
              {r.returned_on ? ` · ${t("rental_returned")}` : ""}
              {r.billed_until ? ` · ${t("rental_billed_until")} ${fmtDate(r.billed_until)}` : ""}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {r.deposit > 0 && !r.deposit_paid_on && (
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => deposit(r, "paid")} data-testid={`rental-deposit-paid-${r.id}`}>
                {t("deposit_received")}
              </Button>
            )}
            {r.deposit_paid_on && !r.deposit_refunded_on && (
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => deposit(r, "refunded")} data-testid={`rental-deposit-refund-${r.id}`}>
                {t("deposit_refund")}
              </Button>
            )}
            {r.deposit_refunded_on && <span className="text-[11px] text-emerald-700 font-semibold">{t("deposit_refunded")}</span>}
            {r.deposit_paid_on && (
              <Button size="sm" variant="ghost" className="h-7" title={t("deposit_receipt")}
                onClick={() => openDepositPdf(r)} data-testid={`rental-deposit-pdf-${r.id}`}>
                <FileText className="h-3.5 w-3.5" />
              </Button>
            )}
            {!r.returned_on && (
              <Button size="sm" variant="ghost" className="h-7" title={t("rental_return")} onClick={() => giveBack(r)} data-testid={`rental-return-${r.id}`}>
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
            )}
            {!r.billed_until && (
              <Button size="sm" variant="ghost" className="h-7" onClick={() => remove(r)} data-testid={`rental-delete-${r.id}`}>
                <Trash2 className="h-3.5 w-3.5 text-rose-500" />
              </Button>
            )}
          </span>
        </div>
      ))}

      <Dialog open={!!add} onOpenChange={(o) => !o && setAdd(null)}>
        <DialogContent data-testid="rental-add-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("rental_add")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">{t("rental_item")}</Label>
              <Select value={add?.item_id || ""} onValueChange={(v) => setAdd({ ...add, item_id: v })}>
                <SelectTrigger data-testid="rental-select-item"><SelectValue placeholder={t("rental_item")} /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} · {i.names?.nl || i.names?.en} · {money(i.price)}/{t(`pricing_${i.pricing}`).toLowerCase()}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs">{t("art_qty")}</Label>
                <Input type="number" min="1" step="1" value={add?.qty || 1} onChange={(e) => setAdd({ ...add, qty: e.target.value })} data-testid="rental-add-qty" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("from")}</Label>
                <Input type="date" value={add?.start || ""} onChange={(e) => setAdd({ ...add, start: e.target.value })} data-testid="rental-add-start" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("until_date")}</Label>
                <Input type="date" value={add?.end || ""} onChange={(e) => setAdd({ ...add, end: e.target.value })} data-testid="rental-add-end" /></div>
            </div>
            {free && (
              <p className={`text-[12px] font-semibold ${free.free > 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="rental-availability">
                {free.free} / {free.owned} {t("rental_available")}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdd(null)}>{t("cancel")}</Button>
            <Button onClick={save} data-testid="rental-add-confirm"
              disabled={busy || !add?.item_id || !add?.start || !add?.end || add.end <= add.start || (free && Number(add.qty) > free.free)}>
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
